import { FC, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import { Avatar, Button, Icon } from '../../../shared/ui';
import { IconMap } from '../../../shared/sprite';
import { cn } from '@distribute/frontend/utils';
import { callModel } from '../model';
import { subscriptionModel } from '../../subscription';
import { useAIExceed } from '../../subscription/hooks';

type Props = {
  participants: string[];
};

export const CallCopyFollowUp: FC<Props> = ({ participants }) => {
  const dispatch = useDispatch();
  const isFollowUpEmailLoading = useSelector(
    callModel.selectors.selectIsFollowUpEmailLoading
  );

  const isAIExceed = useAIExceed();

  const handleGenerarateFollowUp = useCallback(
    (value: string) => {
      if (isAIExceed) {
        dispatch(subscriptionModel.actions.setIsShowPaywallModal(true));
        return;
      }

      dispatch(
        callModel.actions.generateFollowUpEmail({
          participant: value === 'all' ? null : value,
        })
      );
    },
    [dispatch, isAIExceed]
  );

  const items = useMemo(() => {
    return [
      {
        id: 'all',
        label: (
          <span className="text-md font-medium truncate text-gray-900">
            All participants
          </span>
        ),
        icon: (
          <Icon
            glyph={IconMap.Users02}
            width={20}
            className="flex-shrink-0 text-gray-500"
          />
        ),
        onClick: () => handleGenerarateFollowUp('all'),
      },
      ...participants.map((participant) => ({
        id: participant,
        label: (
          <span className="text-md truncate font-normal text-gray-600">
            {participant}
          </span>
        ),
        icon: (
          <Avatar
            displayName={participant}
            size="xs"
            className="border border-gray-100"
          />
        ),
        onClick: () => handleGenerarateFollowUp(participant),
      })),
    ];
  }, [participants, handleGenerarateFollowUp]);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button
          loading={isFollowUpEmailLoading}
          variant="text"
          color="secondary"
          size="sm"
          className="!h-9 !px-3 !font-semibold sm:hidden"
        >
          Copy Follow-Up for
          <Icon
            glyph={IconMap.Gmail1}
            width={20}
            className="ml-1 flex-shrink-0"
          />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        sideOffset={4}
        align="start"
        className={cn(
          `flex flex-col gap-0.25 border rounded-lg bg-base-white border-gray-200 focus:outline-none overflow-hidden px-1.5 py-1 z-10 shadow-lg max-h-60 max-w-76 overflow-y-auto`
        )}
      >
        {items.map((item) => (
          <DropdownMenu.Item
            key={`${item.id} + ${item.label}`}
            onSelect={item.onClick}
            className="outline-none px-2 py-2.5 text-sm font-medium text-gray-900 flex flex-nowrap items-center gap-2 hover:bg-gray-50 focus:bg-gray-50 rounded-md cursor-pointer"
          >
            {item.icon}
            {item.label}
          </DropdownMenu.Item>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};
